import { useCallback, useRef, useState } from 'react';
import {
  transactionExportFiltersSchema,
  type TransactionExportFilters,
} from '@mls-wallet/shared-types';
import denApi from '@/lib/api/denApi';

/** Download every matching token movement from the authenticated CSV endpoint. */
export function useExportTransactions() {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const exportTransactions = useCallback(
    async (filters: TransactionExportFilters): Promise<void> => {
      // The ref closes the gap before React disables the button on a rapid click.
      if (inFlight.current) return;
      inFlight.current = true;
      setIsExporting(true);
      setError(null);
      let objectUrl: string | null = null;
      try {
        const appliedFilters = transactionExportFiltersSchema.parse(filters);
        const csv = await denApi.get<Blob, TransactionExportFilters>(
          'transactions/historic/export',
          appliedFilters,
          { responseType: 'blob' }
        );
        objectUrl = URL.createObjectURL(csv);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = 'token-movements.csv';
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
      } catch {
        setError(
          'Unable to export transactions. Please try again, or select a shorter date range or one account.'
        );
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        inFlight.current = false;
        setIsExporting(false);
      }
    },
    []
  );

  return { exportTransactions, isExporting, error };
}
